import { sum } from "lodash";
import { FC } from "react";
import { Bar, BarChart, CartesianGrid, DefaultZIndexes, Label, ReferenceLine, ResponsiveContainer, Tooltip, TooltipContentProps, XAxis, YAxis } from "recharts";
import { BarRectangleItem } from "recharts/types/cartesian/Bar";
import { NameType, ValueType } from "recharts/types/component/DefaultTooltipContent";
import { ScoreProviderCountModel } from "@app/projects/network-assessment/provider-hub/interfaces";
import { formatCountTick, formatScoreTick, getMinBarHeight, getYAxisTicks, padYAxis } from "@app/projects/network-assessment/provider-hub/pci-dashboard/providerScoreBarChartHelpers";
import { QesColors } from "@app/shared/qes-standards/colors";

interface ProviderScoreBarChartProps {
  data: ScoreProviderCountModel[];
  /** @param score An integer 0-5, where 0 represents score of "Unknown" */
  onBarClick?: (score: number) => void;
  xAxisTitle: string;
  yAxisMax?: number;
  yAxisTitle: string;
}

interface ScoreChartDatum {
  providerCount: number;
  score: number;
}

// Recharts leaves the Bar click payload untyped; this shape matches ScoreChartDatum above.
interface ScoreBarRectangle extends BarRectangleItem {
  payload: ScoreChartDatum;
}

/** 0 representing "Unknown" - see ScoreProviderCountModel. */
const SCORES = [0, 1, 2, 3, 4, 5];

const Y_AXIS_TICK_COUNT = 6;

/** Place the grid lines above highlighted under cursor rectangle but still behind the bars */
const GRID_Z_INDEX = (DefaultZIndexes.cursorRectangle + DefaultZIndexes.bar) / 2;
/** Keep the average score reference line under bars at all times. */
const REFERENCE_LINE_Z_INDEX = DefaultZIndexes.bar - 1;

const renderProviderCountTooltip = ({ active, payload }: TooltipContentProps<ValueType, NameType>) => {
  if (!active || !payload?.length) {
    return null;
  }
  return (
    <div className="pci-dashboard__score-tooltip" data-testid="pci-dashboard__score-chart-tooltip">
      {Number(payload[0].value).toLocaleString()}
    </div>
  );
};

const ProviderScoreBarChart: FC<ProviderScoreBarChartProps> = (props: ProviderScoreBarChartProps) => {
  const chartData: ScoreChartDatum[] = SCORES.map(score => ({
    providerCount: props.data.find(s => s.score === score)?.providerCount ?? 0,
    score,
  }));

  const scoredData = chartData.filter(s => s.score > 0);
  const totalScoredProviders = sum(scoredData.map(s => s.providerCount));
  const averageScore = totalScoredProviders
    ? sum(scoredData.map(s => s.score * s.providerCount)) / totalScoredProviders
    : 0;

  const yAxisDataMax = props.yAxisMax ?? Math.max(1, ...chartData.map(s => s.providerCount)) ?? 1;
  const yAxisTicks = getYAxisTicks(yAxisDataMax, Y_AXIS_TICK_COUNT);
  const yAxisDomainMax = padYAxis(Math.max(...yAxisTicks, yAxisDataMax));

  return (
    <ResponsiveContainer width="100%" height="100%">
      <BarChart data={chartData} margin={{ bottom: 40, left: 30, right: 30, top: 20 }} barCategoryGap="30%">
        <XAxis dataKey="score" type="number" domain={[-0.5, 5.5]} ticks={SCORES} tickFormatter={formatScoreTick}>
          <Label value={props.xAxisTitle} position="bottom" style={{ fontWeight: "bold" }} />
        </XAxis>
        <YAxis
          key={yAxisTicks.join("-")}
          axisLine={false}
          tickLine={false}
          allowDecimals={false}
          domain={[0, yAxisDomainMax]}
          ticks={yAxisTicks}
          tickFormatter={formatCountTick}
        >
          <Label value={props.yAxisTitle} angle={-90} position="insideLeft" style={{ fontWeight: "bold", textAnchor: "middle" }} />
        </YAxis>
        <Tooltip
          cursor={{ fill: QesColors.QesPrimaryGray3 }}
          content={renderProviderCountTooltip}
        />
        {averageScore > 0 && (
          <ReferenceLine
            x={averageScore}
            stroke="black"
            strokeDasharray="3 3"
            zIndex={REFERENCE_LINE_Z_INDEX}
            label={{ position: "insideTopLeft", style: { fontWeight: "bold" }, value: `Avg ${averageScore.toFixed(1)}` }}
          />
        )}
        <CartesianGrid horizontal={true} vertical={false} syncWithTicks={true} zIndex={GRID_Z_INDEX} />
        <Bar
          dataKey="providerCount"
          fill={QesColors.QesTertiaryBlue1}
          activeBar={{ fill: QesColors.Blue500 }}
          radius={[6, 6, 0, 0]}
          minPointSize={getMinBarHeight}
          cursor="pointer"
          onClick={(data: ScoreBarRectangle) => props.onBarClick?.(data.payload.score)}
        />
      </BarChart>
    </ResponsiveContainer>
  );
};

export default ProviderScoreBarChart;
